import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { RefreshCw, Smartphone, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useWhatsApp } from '../contexts/WhatsAppContext';

const WhatsAppQRCode = () => {
  const { whatsappConnected } = useWhatsApp();
  const [qrCode, setQrCode] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  // QR_SEGURO_CARD_V2
  const [safety, setSafety] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [releaseMessage, setReleaseMessage] = useState(null);
    const [reconnecting, setReconnecting] = useState(false);
    const [reconnectMessage, setReconnectMessage] = useState(null);

  // NUMERO_WHATSAPP_NO_CARD_V2
  const [connectionInfo, setConnectionInfo] =
    useState({
      connected: false,
      connectedNumber: null,
      connectedName: null
    });

  const fetchQRCode = async () => {
    try {
      setError(null);

      const API_URL =
        process.env.REACT_APP_API_URL ||
        '/api';

      const [
        statusResponse,
        qrResponse
      ] = await Promise.all([
        fetch(
          `${API_URL}/whatsapp/status`,
          {
            credentials: 'include'
          }
        ),
        fetch(
          `${API_URL}/whatsapp/qrcode`,
          {
            credentials: 'include'
          }
        )
      ]);

      if (
        !statusResponse.ok ||
        !qrResponse.ok
      ) {
        throw new Error(
          'Não foi possível consultar o WhatsApp'
        );
      }

      const statusData =
        await statusResponse.json();

      const qrData =
        await qrResponse.json();

        // CONSULTAR_PROTECAO_QR_V2
        const safetyResponse =
          await fetch(
            API_URL + '/whatsapp/safety-status',
            {
              credentials: 'include'
            }
          );

        if (safetyResponse.ok) {
          const safetyData =
            await safetyResponse.json();

          setSafety(
            safetyData.safety || null
          );
        }

      setConnectionInfo({
        connected:
          statusData.connected === true,
        connectedNumber:
          statusData.connectedNumber ||
          null,
        connectedName:
          statusData.connectedName ||
          null
      });

      if (statusData.connected === true) {
        setQrCode(null);
        setStatus('connected');
        return;
      }

      if (qrData.hasQrCode) {
        setQrCode(qrData.qrCode);
        setStatus('ready');
      } else {
        setQrCode(null);
        setStatus('no_qrcode');
      }
    } catch (err) {
      setError(
        err.message ||
        'Erro ao consultar WhatsApp'
      );

      setStatus('error');
    }
  };

  useEffect(() => {
    fetchQRCode();
    
    // Atualizar QR Code a cada 5 segundos
    const interval = setInterval(() => {
      fetchQRCode();
    }, 5000);

  return () => clearInterval(interval);
  }, []);

  const handleSafetyRelease = async () => {
      const confirmado = window.confirm(
        'ATENÇÃO: gerar um novo QR substitui a sessão atual do WhatsApp. Use somente se a reconexão com a sessão existente falhar. Deseja continuar?'
      );

      if (!confirmado) {
        return;
      }

    try {
      setReleasing(true);
      setReleaseMessage(null);

      const API_URL =
        process.env.REACT_APP_API_URL ||
        '/api';

      const response =
        await fetch(
          API_URL + '/whatsapp/restart',
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              confirmRelease: true
            })
          }
        );

      const data =
        await response.json();

      if (!response.ok || data.success !== true || data.result?.success === false) {
        if (data.releaseAvailableAt) {
          throw new Error(
            'Proteção ativa até ' +
            new Date(
              data.releaseAvailableAt
            ).toLocaleString(
              'pt-BR'
            )
          );
        }

        throw new Error(
          data.error || data.result?.message ||
          'Não foi possível gerar o QR. É necessário usuário master.'
        );
      }

      setReleaseMessage(
        'Nova sessão solicitada. Aguarde o QR e escaneie pelo celular.'
      );

      setTimeout(
        fetchQRCode,
        2000
      );
    } catch (err) {
      setReleaseMessage(
        err.message
      );
    } finally {
      setReleasing(false);
    }
  };

    const handleReconnectExistingSession = async () => {
      try {
        setReconnecting(true);
        setReconnectMessage(null);
        setError(null);

        const API_URL =
          process.env.REACT_APP_API_URL ||
          '/api';

        const response = await fetch(
          API_URL + '/admin/restart-whatsapp',
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.error ||
            'Não foi possível reconectar o WhatsApp.'
          );
        }

        setReconnectMessage(
          'Reconexão solicitada com a sessão existente. Aguarde alguns segundos.'
        );

        setTimeout(
          fetchQRCode,
          2500
        );
      } catch (err) {
        setReconnectMessage(
          err.message ||
          'Não foi possível reconectar o WhatsApp.'
        );
      } finally {
        setReconnecting(false);
      }
    };


    const conectado =
    connectionInfo.connected === true ||
    whatsappConnected === true;

  const formatarTelefone = valor => {
    const numeros =
      String(valor || '')
        .replace(/\D/g, '');

    if (
      numeros.length === 13 &&
      numeros.startsWith('55')
    ) {
      return (
        '+55 (' +
        numeros.slice(2, 4) +
        ') ' +
        numeros.slice(4, 9) +
        '-' +
        numeros.slice(9)
      );
    }

    if (
      numeros.length === 12 &&
      numeros.startsWith('55')
    ) {
      return (
        '+55 (' +
        numeros.slice(2, 4) +
        ') ' +
        numeros.slice(4, 8) +
        '-' +
        numeros.slice(8)
      );
    }

    return numeros
      ? '+' + numeros
      : 'Número não identificado';
  };


  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Smartphone className="w-5 h-5" />
          Conexão WhatsApp
        </h3>
        <button
          onClick={fetchQRCode}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title="Atualizar status"
        >
          <RefreshCw className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      <div className="space-y-4">
        {/* Status */}
        <div className="flex items-center gap-2">
          {conectado ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-green-600 dark:text-green-400 font-medium">Conectado</span>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-600 dark:text-red-400 font-medium">Desconectado</span>
            </>
          )}
        </div>

          {!conectado && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
              <button
                type="button"
                onClick={handleReconnectExistingSession}
                disabled={reconnecting}
                className="w-full rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {reconnecting
                  ? 'Reconectando...'
                  : 'Reconectar sessão existente'}
              </button>

              <p className="mt-2 text-xs text-blue-700 dark:text-blue-300">
                Reutiliza a autenticação atual. Não gera QR Code e não substitui a sessão.
              </p>

              {reconnectMessage && (
                <p className="mt-2 text-sm font-medium text-blue-700 dark:text-blue-300">
                  {reconnectMessage}
                </p>
              )}
            </div>
          )}

        {/* QR Code */}
        {status === 'loading' && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        )}

        {status === 'error' && (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {status === 'no_qrcode' && !conectado && (
            <div className="text-center py-8 text-gray-600 dark:text-gray-400">
              <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />

              {safety?.status === 'open' ? (
                <>
                  <p className="font-semibold text-amber-600 dark:text-amber-400">
                    Proteção do WhatsApp ativa
                  </p>

                  <p className="mt-2 text-sm">
                    Primeiro tente reconectar a sessão existente. Gere um novo QR somente se a sessão tiver sido realmente encerrada.
                  </p>

                  {safety.releaseAvailableAt && (
                    <p className="mt-2 text-sm font-medium">
                      Disponível em:{' '}
                      {new Date(
                        safety.releaseAvailableAt
                      ).toLocaleString('pt-BR')}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={handleSafetyRelease}
                    disabled={
                      releasing || reconnecting
                    }
                    className="mt-4 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {releasing
                      ? 'Liberando...'
                      : 'Gerar novo QR (substitui a sessão)'}
                  </button>
                </>
              ) : (
                <>
                  <p>Nenhum QR Code disponível</p>
  <button type="button"
    onClick={handleSafetyRelease}
    disabled={releasing || reconnecting}
    className="mt-4 rounded-lg bg-amber-600 px-4 py-2 font-semibold text-white disabled:opacity-50">
    {releasing ? 'Gerando QR...' : 'Gerar novo QR Code'}
  </button>
                  <p className="mt-1 text-sm">
                    Primeiro tente reconectar a sessão existente. Gere um novo QR somente se a sessão tiver sido realmente encerrada.
                  </p>
                </>
              )}

              {releaseMessage && (
                <p className="mt-3 text-sm font-medium text-amber-600 dark:text-amber-400">
                  {releaseMessage}
                </p>
              )}
            </div>
          )}

          {status === 'ready' && qrCode && !conectado && (
          <div className="flex flex-col items-center">
            <div className="bg-white p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700">
              <QRCodeSVG value={qrCode} size={200} />
            </div>
            <div className="mt-4 text-center space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Escaneie o QR Code com seu WhatsApp
              </p>
              <ol className="text-xs text-gray-500 dark:text-gray-500 text-left space-y-1">
                <li>1. Abra o WhatsApp no seu celular</li>
                <li>2. Vá em: Aparelhos conectados → Conectar um aparelho</li>
                <li>3. Escaneie o QR Code acima</li>
              </ol>
            </div>
          </div>
        )}

        {conectado && (
          <div className="text-center py-8 text-green-600 dark:text-green-400">
            <CheckCircle className="w-12 h-12 mx-auto mb-2" />
            <p className="font-medium">WhatsApp conectado com sucesso!</p>

              <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 text-left dark:border-green-800 dark:bg-green-900/20">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Número conectado
                </p>

                <p className="mt-1 text-lg font-bold text-gray-900 dark:text-white">
                  {formatarTelefone(connectionInfo.connectedNumber)}
                </p>

                {connectionInfo.connectedName && (
                  <>
                    <p className="mt-3 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Nome da conta
                    </p>

                    <p className="mt-1 font-medium text-gray-900 dark:text-white">
                      {connectionInfo.connectedName}
                    </p>
                  </>
                )}
              </div>

              <p className="mt-3 text-sm font-medium text-amber-600 dark:text-amber-400">
                Confirme este número antes de ativar os envios automáticos.
              </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppQRCode;
